import { useState, useCallback } from 'react';
import { getApiUrl, getAuthHeaders } from '@/utils/authUtils';

/**
 * Custom hook for making authenticated API requests
 * Automatically handles:
 * - API URL construction (prepends base URL to /api paths)
 * - Authentication headers (Bearer token from localStorage)
 * - Credentials include for cookies
 * - Loading and error states
 * - Cache-busting for GET requests
 * 
 * @example
 * const { data, loading, error, get, post } = useFetch();
 * 
 * // GET request
 * const result = await get('/api/users/me');
 * 
 * // POST request with body
 * const result = await post('/api/auth/login', { email, password });
 */
export const useFetch = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (endpoint, options = {}) => {
    setLoading(true);
    setError(null);

    try {
      const apiUrl = getApiUrl();
      const authHeaders = getAuthHeaders();
      
      // Build the full URL
      let url = endpoint.startsWith('/api') ? `${apiUrl}${endpoint}` : endpoint;
      
      // Add cache-busting for GET requests
      if (!options.method || options.method === 'GET') {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}_t=${Date.now()}`;
      }

      // Build headers
      const headers = {
        ...authHeaders,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...options.headers
      };

      // Add Content-Type for requests with body (objects get stringified
      // below; pre-stringified JSON strings pass through untouched —
      // double-stringifying them broke every caller that did its own
      // JSON.stringify, e.g. the Thank-You Wall form)
      if (options.body && !(options.body instanceof FormData)) {
        headers['Content-Type'] = headers['Content-Type'] || 'application/json';
      }

      const fetchOptions = {
        ...options,
        credentials: 'include',
        headers,
        body: typeof options.body === 'string' || options.body instanceof FormData
          ? options.body
          : (options.body ? JSON.stringify(options.body) : undefined)
      };

      const response = await fetch(url, fetchOptions);
      
      // Parse response
      const responseText = await response.text();
      let responseData;
      
      try {
        responseData = responseText ? JSON.parse(responseText) : null;
      } catch {
        responseData = responseText;
      }

      if (!response.ok) {
        const errorMessage = responseData?.detail || responseData?.message || `Request failed (${response.status})`;
        throw new Error(errorMessage);
      }

      setData(responseData);
      return { data: responseData, error: null };
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Convenience methods
  const get = useCallback((endpoint, options = {}) => {
    return fetchData(endpoint, { ...options, method: 'GET' });
  }, [fetchData]);

  const post = useCallback((endpoint, body, options = {}) => {
    return fetchData(endpoint, { ...options, method: 'POST', body });
  }, [fetchData]);

  const put = useCallback((endpoint, body, options = {}) => {
    return fetchData(endpoint, { ...options, method: 'PUT', body });
  }, [fetchData]);

  const del = useCallback((endpoint, options = {}) => {
    return fetchData(endpoint, { ...options, method: 'DELETE' });
  }, [fetchData]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { data, loading, error, fetchData, get, post, put, del, reset };
};

/**
 * Standalone fetch function for use outside React components
 * @example
 * const { data, error } = await apiFetch('/api/users/me');
 */
export const apiFetch = async (endpoint, options = {}) => {
  // Use relative URLs for /api endpoints so the browser resolves against
  // window.location.origin. This ensures production calls go to the production
  // server, not the preview URL baked in REACT_APP_BACKEND_URL at build time.
  let url = endpoint;
  if (endpoint.startsWith('/api')) {
    // In development/preview, REACT_APP_BACKEND_URL works fine.
    // In production, relative URL ensures same-origin requests.
    // Using relative URL is correct in both environments because
    // the ingress routes /api to the backend.
    url = endpoint;
  }
  
  const authHeaders = getAuthHeaders();
  
  if (!options.method || options.method === 'GET') {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}_t=${Date.now()}`;
  }

  const isFormData = options.body instanceof FormData;
  const isStringBody = typeof options.body === 'string';

  const headers = {
    ...authHeaders,
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    ...options.headers
  };

  // Pre-stringified JSON strings must pass through untouched — re-running
  // JSON.stringify double-encodes them (and skipped the Content-Type header),
  // which made FastAPI 422 every caller that did its own JSON.stringify.
  if (options.body && !isFormData) {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }

  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers,
      body: isFormData || isStringBody
        ? options.body
        : (options.body ? JSON.stringify(options.body) : undefined)
    });

    // Defensive: a misbehaving service worker / proxy can occasionally
    // consume the response body before we read it. We guard against the
    // "body stream already read" crash so the admin always gets a clean
    // error message instead of a stack trace toast.
    let data = null;
    try {
      if (!response.bodyUsed) {
        const responseText = await response.text();
        try {
          data = responseText ? JSON.parse(responseText) : null;
        } catch {
          data = responseText;
        }
      }
    } catch {
      // Body unreadable — fall back to status-only error reporting.
      data = null;
    }

    if (!response.ok) {
      // FastAPI returns either {"detail": "..."} or {"detail": [...]} for
      // 422 validation errors. Coerce arrays/objects into a readable string
      // so toast.error() never receives an object (which would render "[object Object]").
      let errMsg;
      if (Array.isArray(data?.detail)) {
        errMsg = data.detail.map((e) => e.msg || JSON.stringify(e)).join('; ');
      } else if (typeof data?.detail === 'string') {
        errMsg = data.detail;
      } else if (typeof data === 'string' && data) {
        errMsg = data;
      } else {
        errMsg = `Request failed (${response.status})`;
      }
      return { data: null, error: errMsg, status: response.status };
    }
    return { data, error: null, status: response.status };
  } catch (err) {
    // Network error / abort / parse failure — no HTTP status available
    return { data: null, error: err.message, status: 0 };
  }
};

export default useFetch;
